import { z } from 'zod';
import { ConfidenceBand, DataClassification } from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';

/**
 * The metric response contract.
 *
 * Source: Metric Catalog → "Metric Response Contract" sheet (14 fields).
 *
 * The governing rule is that no number appears in this product without the
 * apparatus needed to challenge it: which definition produced it, over what
 * period, from which source, how fresh, how trustworthy, and where you can go
 * to see the detail. A bare number on a tile is a defect, not a shortcut.
 *
 * Two consequences worth stating explicitly:
 *
 *  - `status` is not decoration. A metric whose source failed must render as
 *    `Unavailable`, never as zero. "No missing source represented as zero" is a
 *    hard rule from the delivery prompt, and it lives here because the contract
 *    is the only place that can guarantee it.
 *
 *  - `value` is nullable. That is the schema-level admission that not knowing is
 *    a legitimate state — which is what makes the previous rule enforceable.
 */

export const MetricStatus = z.enum(['Available', 'Partial', 'Stale', 'Unavailable', 'Error']);
export type MetricStatus = z.infer<typeof MetricStatus>;

/** How a value reached us. Drives the SourceBadge and the freshness story. */
export const SourceMode = z.enum(['FileFeed', 'Manual', 'Calculated', 'DirectConnector']);
export type SourceMode = z.infer<typeof SourceMode>;

export const MetricUnit = z.enum([
  'count',
  'percent', // stored as a ratio 0–1; formatted as % in the UI
  'ratio',
  'currency',
  'days',
  'hours',
  'score', // 0–100
  'fte',
  'itemsPerPeriod',
  'deploymentsPerPeriod',
  'currencyPerRelease',
  // Payback and ramp periods. Days would imply a precision a scenario does not
  // have, and a bare count leaves the reader guessing what is being counted.
  'months',
  'text',
]);
export type MetricUnit = z.infer<typeof MetricUnit>;

export const DesiredDirection = z.enum(['Up', 'Down', 'Context']);
export type DesiredDirection = z.infer<typeof DesiredDirection>;

/**
 * Money is decimal plus currency, never a bare float. Amounts cross the wire as
 * strings so that no rounding happens in JSON parsing.
 */
export const Money = z.object({
  amount: z.string().regex(/^-?\d+(\.\d+)?$/, 'expected a decimal string'),
  currency: z.string().length(3),
});
export type Money = z.infer<typeof Money>;

/** A drill-down the caller is authorised to follow from this metric. */
export const AllowedDrilldown = z.object({
  dimension: z.string().min(1),
  label: z.string().min(1),
  route: z.string().min(1),
});
export type AllowedDrilldown = z.infer<typeof AllowedDrilldown>;

export const MetricValue = z.object({
  /** Governed Metric ID, e.g. 'MET-003'. No metric may be displayed without one. */
  metricId: z.string().regex(/^MET-\d{3}$/),
  label: z.string().min(1),
  /** The entity this measurement is about — product, hierarchy node, application. */
  subjectId: Uuid.nullable(),
  subjectType: z.string().nullable(),

  /** Null whenever `status` is not `Available` or `Partial`. Never coerce to 0. */
  value: z.union([z.number(), z.string(), Money]).nullable(),
  unit: MetricUnit,

  periodStart: IsoDate,
  periodEnd: IsoDate,

  /** Prior period or comparison scenario, when the caller asked for one. */
  comparisonValue: z.union([z.number(), z.string(), Money]).nullable(),
  comparisonLabel: z.string().nullable(),

  status: MetricStatus,
  /** Formula/model version. Two runs of the same version must agree. */
  definitionVersion: z.string().min(1),
  sourceMode: SourceMode,
  sourceRunId: Uuid.nullable(),
  refreshedAt: Timestamp.nullable(),

  qualityScore: z.number().min(0).max(100).nullable(),
  confidence: ConfidenceBand.nullable(),

  desiredDirection: DesiredDirection,
  sensitivity: DataClassification,
  allowedDrilldowns: z.array(AllowedDrilldown),

  /**
   * Why a metric is not `Available`. Surfaced in the tile so the user is told
   * "the finance feed has not run since Tuesday" rather than being shown a dash.
   */
  unavailableReason: z.string().nullable(),
});
export type MetricValue = z.infer<typeof MetricValue>;

/**
 * The governed definition behind a metric, shown in the MetricDefinitionDrawer.
 * Every KPI tile must be able to open this.
 */
export const MetricDefinition = z.object({
  metricId: z.string().regex(/^MET-\d{3}$/),
  domain: z.string().min(1),
  name: z.string().min(1),
  businessDefinition: z.string().min(1),
  grain: z.string().min(1),
  formula: z.string().min(1),
  unit: MetricUnit,
  desiredDirection: DesiredDirection,
  businessOwner: z.string().min(1),
  sourceMode: SourceMode,
  dimensions: z.array(z.string()),
  thresholdLogic: z.string().nullable(),
  primaryScreens: z.array(z.string()),
  sensitivity: DataClassification,
  definitionVersion: z.string().min(1),
});
export type MetricDefinition = z.infer<typeof MetricDefinition>;

/**
 * Chart payload.
 *
 * `tableRows` is not optional. Every chart in this product ships with an
 * accessible table equivalent, and making it a required field of the DTO means
 * a chart physically cannot be rendered without one.
 */
export const ChartSeries = z.object({
  seriesId: z.string().min(1),
  label: z.string().min(1),
  points: z.array(
    z.object({
      x: z.union([z.string(), z.number()]),
      y: z.number().nullable(),
    }),
  ),
});
export type ChartSeries = z.infer<typeof ChartSeries>;

export const ChartPayload = z.object({
  chartId: z.string().min(1),
  title: z.string().min(1),
  /** Text description for screen readers and for the chart's caption. */
  accessibleSummary: z.string().min(1),
  series: z.array(ChartSeries),
  /** Required accessible alternative. See note above. */
  tableColumns: z.array(z.object({ key: z.string(), label: z.string() })),
  tableRows: z.array(z.record(z.union([z.string(), z.number(), z.null()]))),
  metricIds: z.array(z.string()),
  status: MetricStatus,
  refreshedAt: Timestamp.nullable(),
});
export type ChartPayload = z.infer<typeof ChartPayload>;

/**
 * A value that may be partly attributed. Used wherever cost, capacity or benefit
 * is rolled up across many-to-many relationships.
 *
 * `unallocated` is a required field rather than an inferred remainder, because
 * the specification forbids silently apportioning what has not been allocated.
 * The UI is expected to show it.
 */
export const AllocatedTotal = z.object({
  allocated: Money,
  unallocated: Money,
  total: Money,
  /** 0–1. How much of `total` carries an approved allocation. */
  allocationCoverage: z.number().min(0).max(1),
  basis: z.string().nullable(),
});
export type AllocatedTotal = z.infer<typeof AllocatedTotal>;
